import { Icon, usePrompt } from '@ur-solutions/react-components'
import omit from 'lodash/omit'
import type {
  DieselCardForm,
  EditVehicleDetailsModalResolve,
  VehicleDetailInput,
  VehicleDetailNode} from '@/modules/vehicles';
import {
  sup
} from '@/modules/vehicles'
import type { ReactNode} from 'react';
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { useVehicleDetailFormatter } from '../hooks'
import { useVehicleMutations } from '../mutations.hooks'
import { EditVehicleDetailsModal } from './EditVehicleDetailsModal'
import { usePermissions } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useTranslate } from '@ur-solutions/react-hooks'

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 1rem;
  background-color: ${props => props.theme.colors.white};
  box-shadow: ${props => props.theme.shadow.default};
  border-radius: 5px;
`
const DetailCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${props => props.theme.colors.gray8};
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;

  h4 {
    margin: 0;
  }
`

export const DetailCardRow = styled.div`
  display: flex;
  margin: 0.3rem 0;

  div {
    flex: 1;
  }

  div:last-child {
    text-align: right;
  }

  font-weight: 500;

  .unit {
    margin-left: 0.2rem;
    color: ${props => props.theme.colors.gray4};
    font-weight: 400;
  }
`

interface DetailCardProps {
  title: string
  fields: VehicleDetailInput[]
  vehicle: VehicleDetailNode
}

export const DetailCard: React.FC<DetailCardProps> = ({
  title,
  fields,
  vehicle,
}) => {
  const addPrompt = usePrompt()
  const translations = useTranslate({
    pinCode: 'vehicles.pin-code',
  })
  const {
    mutations: {
      patch: patchVehicle,
      patchDieselCard,
      createDieselCard,
      deleteDieselCard,
    },
  } = useVehicleMutations()
  const { formatValue } = useVehicleDetailFormatter(vehicle)
  const { hasPermissions } = usePermissions()
  const canEdit = hasPermissions(PERMISSIONS.vehicles.change.vehicle, true)

  const toSuperscript = (num: number) => {
    return num
      .toString()
      .split('')
      .map(d => sup[parseInt(d)])
      .join('')
  }

  const rows = useMemo(() => {
    return (
      fields
        ?.filter(
          field => vehicle[field.key] !== null && vehicle[field.key] !== ''
        )
        .flatMap(field => {
          if (field.key === 'dieselCardNumber') {
            return (
              (vehicle?.dieselCards as (DieselCardForm | null)[] | null)
                ?.filter(el => el !== null)
                ?.map((el, index) => (
                  <React.Fragment key={`${field.key}-${index}`}>
                    <DetailCardRow>
                      <div>
                        {field.label}
                        {index > 0 && toSuperscript(index + 1)}
                      </div>
                      <div>{el.number}</div>
                    </DetailCardRow>
                    <DetailCardRow>
                      <div>
                        {translations.pinCode}
                        {index > 0 && toSuperscript(index + 1)}
                      </div>
                      <div>{el.pinCode}</div>
                    </DetailCardRow>
                  </React.Fragment>
                )) || []
            )
          } else {
            const value = formatValue(field) as ReactNode
            return (
              <DetailCardRow key={field.key}>
                <div>{field.label}</div>
                <div>
                  {value}
                  {field.unit && <span className="unit">{field.unit}</span>}
                </div>
              </DetailCardRow>
            )
          }
        }) || []
    )
  }, [fields, vehicle, formatValue, translations])

  const handleClick = async (id: string) => {
    try {
      await deleteDieselCard({
        variables: { id },
      })
    } catch (error) {
      console.error('Failed to delete diesel card:', error)
    }
  }

  async function handleEdit() {
    const { data } = await addPrompt<EditVehicleDetailsModalResolve | null>(
      resolve => (
        <EditVehicleDetailsModal
          title={title}
          vehicle={vehicle}
          fields={fields}
          onSubmit={resolve}
          onDelete={async (id: string) => await handleClick(id)}
        />
      )
    )

    if (!data) return
    await patchVehicle({
      variables: {
        id: vehicle.id,
        input: {
          ...omit(data.form, [
            'vin',
            ...fields
              .filter(field => field.readOnly === true)
              .map(field => field.key),
            'dieselCardNumber',
          ]),
        },
      },
    })

    await Promise.all(
      (data.form?.dieselCardNumber as DieselCardForm[])?.map(card => {
        if (card.id) {
          return patchDieselCard({
            variables: {
              id: card.id,
              input: {
                number: card.number,
                pinCode: Number(card.pinCode),
              },
            },
          })
        } else {
          return createDieselCard({
            variables: {
              input: {
                vehicle: vehicle.id,
                number: card.number,
                pinCode: Number(card.pinCode),
              },
            },
          })
        }
      })
    )
  }

  return (
    <Wrapper>
      <DetailCardHeader>
        <h4>{title}</h4>
        {canEdit && (
          <Icon
            icon="edit"
            cursor="pointer"
            color="gray6"
            hoverColor="secondary"
            onClick={() => handleEdit()}
          />
        )}
      </DetailCardHeader>
      {rows}
    </Wrapper>
  )
}
